import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card, Form, Button } from "react-bootstrap";
import axios from "axios";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from "react-fullscreen-loading";
import { toast } from "react-hot-toast";
import Select from "react-select";

export default function EditDocument() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the document ID from the URL
  const [screenloader, setScreenloader] = useState(false);
  const [isReseller, setIsReseller] = useState(false);
  const [documentData, setDocumentData] = useState({});
  const [resellerList, setResellerList] = useState([]);
  const [documentUserList, setDocumentUserList] = useState([]);
  const [user, setUser] = useState(null);
  const [preferredPaymentMethod, setPreferredPaymentMethod] = useState("");
  const [preferredPaymentTerm, setPreferredPaymentTerm] = useState("");
  const [subTotal, setSubTotal] = useState(0);
  const [praAmount, setPraAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [termsAndConditions, setTermsAndConditions] = useState("");

  useEffect(() => {
    getUserProfiles();
    fetchDocumentData();
  }, []);

  useEffect(() => {
    if (user) {
      const selectedProfile = isReseller
        ? resellerList.find((reseller) => reseller.id === user.value)
        : documentUserList.find((documentUser) => documentUser.id === user.value);

      if (selectedProfile) {
        setPreferredPaymentMethod(selectedProfile.preferred_payment_method || "");
        setPreferredPaymentTerm(selectedProfile.preferred_payment_term || "");
      }
    }
  }, [user, isReseller]);

  const getUserProfiles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/user_profiles`, {
        headers: { "Access-Token": localStorage.getItem("accessToken") },
        params: { limit: 100, page: 1, sort_by: "created_at", sort_order: "desc" },
      });
      const userProfiles = response.data.user_profiles;
      setResellerList(userProfiles.filter((user) => user.type?.toLowerCase() === "reseller"));
      setDocumentUserList(userProfiles.filter((user) => user.type?.toLowerCase() === "document_user"));
    } catch {
      toast.error("Failed to fetch user profiles.");
    }
  };

  const fetchDocumentData = async () => {
    try {
      setScreenloader(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/documents/${id}`, {
        headers: { "Access-Token": localStorage.getItem("accessToken") },
      });
      const data = response.data.document;

      setDocumentData(data);
      setUser({ value: data.user_profile_id, label: data.user_profile.name });
      setPreferredPaymentMethod(data.payment_method);
      setPreferredPaymentTerm(data.payment_term);
      setSubTotal(parseFloat(data.sub_total));
      setTotalAmount(parseFloat(data.total));
      setTermsAndConditions(data.terms_and_conditions);
      setIsReseller(data.user_profile.type.toLowerCase() === "reseller");
    } catch {
      toast.error("Failed to fetch document details.");
    } finally {
      setScreenloader(false);
    }
  };

  const handleUpdateDocument = async () => {
    if (!user || !preferredPaymentMethod || !preferredPaymentTerm) {
      toast.error("Please complete all required fields.");
      return;
    }

    const payload = {
      user_profile_id: user.value,
      payment_method: preferredPaymentMethod,
      payment_term: preferredPaymentTerm,
      sub_total: subTotal,
      total: totalAmount,
      terms_and_conditions: termsAndConditions,
    };

    try {
      setScreenloader(true);
      await axios.put(`${process.env.REACT_APP_API_URL}/admin/documents/${id}`, payload, {
        headers: { "Access-Token": localStorage.getItem("accessToken") },
      });
      toast.success("Document updated successfully!");
      navigate("/admin/documents");
    } catch {
      toast.error("Failed to update document.");
    } finally {
      setScreenloader(false);
    }
  };

  const renderUserProfileSelector = () => (
    <div className="my-1">
      <div className="d-flex align-items-center gap-3">
        <label style={{ fontWeight: "bold" }}>Select User Type</label>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className={`btn ${!isReseller ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setIsReseller(false)}
          >
            Document User
          </button>
          <button
            type="button"
            className={`btn ${isReseller ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setIsReseller(true)}
          >
            Reseller
          </button>
        </div>
      </div>
      <div className="my-3">
        <Select
          value={user}
          onChange={(selected) => setUser(selected)}
          options={
            isReseller
              ? resellerList.map((reseller) => ({ value: reseller.id, label: reseller.name }))
              : documentUserList.map((user) => ({ value: user.id, label: user.name }))
          }
        />
      </div>
    </div>
  );

  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <h4>Edit Document</h4>
            <Container fluid className="behind">
              <Card className="mt-4">
                <Card.Body>
                  <form>
                    {renderUserProfileSelector()}

                    <div className="row my-4">
                      {[
                        { label: "Preferred Payment Method", value: preferredPaymentMethod },
                        { label: "Preferred Payment Term", value: preferredPaymentTerm },
                      ].map((field, index) => (
                        <div className="col-lg-6 col-md-6 col-12 d-flex align-items-center mb-3" key={index}>
                          <label style={{ fontWeight: "bold", marginRight: "10px", minWidth: "150px" }}>
                            {field.label}:
                          </label>
                          <p className="form-control-plaintext m-0">{field.value || " "}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <div className="row">
                        <div className="col-lg-4 col-md-6">
                          <label style={{ fontWeight: "bold" }}>Sub-Total:</label>
                          <input
                            type="number"
                            className="form-control"
                            value={subTotal.toFixed(2)}
                            readOnly
                          />
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <label style={{ fontWeight: "bold" }}>PRA @ 5%:</label>
                          <input
                            type="number"
                            className="form-control"
                            value={praAmount.toFixed(2)}
                            readOnly
                          />
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <label style={{ fontWeight: "bold" }}>Total Amount:</label>
                          <input
                            type="number"
                            className="form-control"
                            value={totalAmount.toFixed(2)}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>

                    <div className="my-4">
                      <label style={{ fontWeight: "bold" }}>Terms and Conditions</label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={termsAndConditions}
                        onChange={(e) => setTermsAndConditions(e.target.value)}
                      />
                    </div>

                    <div className="mt-4 text-end">
                      <Button variant="primary" onClick={handleUpdateDocument}>
                        Update Document
                      </Button>
                    </div>
                  </form>
                </Card.Body>
              </Card>
            </Container>
          </div>
        </div>
      </div>
    </div>
  );
}
